import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import type React from "react";

interface Props {
    icon?: string | React.ReactNode;
    words: string | React.ReactNode;
    className?: string;
    variant?: "info" | "destructive" | "warning" | "success" | "muted" | "blue" | "blueSoft";
    children?: React.ReactNode;
    size?: "xs" | "sm" | "md" | "lg";
    button?: React.ReactNode;
}

const callVariants = cva(
    "relative flex flex-col gap-2 shadow-md border-2 dark:border-slate-800 rounded-lg w-full w-full",
    {
        variants: {
            variant: {
                info: "bg-indigo-100 border-indigo-300 text-indigo-700 [&_h5]:text-indigo-800 dark:[&_h5]:text-indigo-100  dark:bg-indigo-900 dark:text-indigo-200 dark:border-indigo-700",
                destructive:
                    "bg-red-100 border-red-300 text-red-700 [&_h5]:text-red-800 dark:[&_h5]:text-red-100    dark:bg-red-900 dark:text-red-200 dark:border-red-700",
                warning:
                    "bg-amber-50 border-amber-500 text-amber-700 [&_h5]:text-amber-800 dark:[&_h5]:text-amber-100    dark:bg-amber-900 dark:text-amber-200 dark:border-amber-700",
                success:
                    "bg-green-100 border-green-300 text-green-700  [&_h5]:text-green-800 dark:[&_h5]:text-green-100  dark:bg-green-900 dark:text-green-200 dark:border-green-700",
                muted: "bg-muted/50 border-muted-foreground/20 text-muted-foreground [&_h5]:text-foreground dark:[&_h5]:text-foreground shadow-none",
                blue: "bg-blue-50 border-blue-300 text-blue-800 [&_h5]:text-blue-900 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-700 dark:[&_h5]:text-blue-100",
                blueSoft:
                    "border-dashed bg-gradient-to-br from-blue-50/90 via-blue-50/60 to-indigo-50/40 border-blue-300/70 text-blue-800 shadow-sm [&_h5]:text-blue-900 dark:from-blue-950/50 dark:via-blue-950/35 dark:to-indigo-950/25 dark:text-blue-200 dark:border-blue-600/45 dark:[&_h5]:text-blue-100",
            },
            size: {
                lg: "text-lg p-4 rounded-lg",
                md: "text-md p-3 rounded-md",
                sm: "text-sm p-2 rounded-md",
                xs: "text-xs p-1 rounded-md",
            },
        },
        defaultVariants: {
            variant: "info",
            size: "md",
        },
    }
);
const Call = ({ icon, words, className, children, variant, size, button }: Props) => {
    const showIcon = icon != null && icon !== "";

    return (
        <section className={cn(callVariants({ variant, size }), className)}>
            <div className="flex flex-row items-center gap-2">
                {showIcon ? (
                    <span
                        className={cn(
                            "shrink-0",
                            size === "xs" ? "[&>svg]:w-5 [&>svg]:h-5" : "",
                            size === "sm" ? "[&>svg]:w-6 [&>svg]:h-6" : "",
                            size === "md" ? "[&>svg]:w-7 [&>svg]:h-7" : "",
                            size === "lg" ? "[&>svg]:w-8 [&>svg]:h-8" : ""
                        )}
                    >
                        {icon}
                    </span>
                ) : null}
                <h5
                    className={cn(
                        "my-auto text-lg",
                        size === "xs" ? "text-xs" : "",
                        size === "sm" ? "text-sm" : "",
                        size === "md" ? "text-md" : "",
                        size === "lg" ? "text-xl" : ""
                    )}
                >
                    {words}
                </h5>
            </div>

            <div className="flex flex-col flex-1 min-w-0">
                <div className={cn("flex flex-col gap-y-1")}>{children}</div>
            </div>
            {button && <div className="mr-auto">{button}</div>}
        </section>
    );
};

export default Call;
